/**
 * Timeline Data Transforms
 *
 * Utilities for converting between data formats:
 * - SplicingPreviewData -> TimelineData
 * - Full history merge (future)
 */

import type { TimelineSegment, TimelineData } from '../types'
import type { SplicingPreviewData } from '@/pages/modules/data/types'

/**
 * Extract label from a record using the configured label field.
 *
 * @param record - The record to extract label from
 * @param labelField - The field name to use for label, or null to disable labels
 * @returns The label string, empty string if labels disabled, or 'Unnamed' if field not found
 */
function getLabel(record: Record<string, unknown>, labelField: string | null | undefined): string {
  // If labelField is null/undefined, labels are disabled - return empty string
  if (labelField === null || labelField === undefined) {
    return ''
  }

  const value = record[labelField]
  if (value === null || value === undefined) {
    // Fallback to 'name' if configured field is empty
    const fallback = record.name
    if (fallback === null || fallback === undefined) {
      return 'Unnamed'
    }
    return String(fallback)
  }
  return String(value)
}

/**
 * Options for transforming splicing preview data to timeline data.
 */
export interface PreviewToTimelineOptions {
  /**
   * The field to use for segment labels, or null to disable labels.
   */
  labelField?: string | null

  /**
   * Whether to prefix "NEW" to the focus segment (the record being activated).
   * - true: Label becomes "NEW" (if no label) or "NEW - {label}"
   * - false/undefined: Use regular label logic
   *
   * This is independent from labelField - even if labelField is null,
   * showNewPrefix will show "NEW" on the focus segment.
   */
  showNewPrefix?: boolean
}

/**
 * Transform SplicingPreviewData into TimelineData for the new timeline component.
 *
 * Creates both BEFORE and AFTER timeline datasets:
 * - BEFORE: Shows records as they currently exist (active state)
 * - AFTER: Shows the result after activation (splices, NEW, continuations)
 *
 * @param previewData - The splicing preview data from the API
 * @param labelFieldOrOptions - Either a label field string (legacy), or options object
 */
export function previewDataToTimelineData(
  previewData: SplicingPreviewData,
  labelFieldOrOptions?: string | null | PreviewToTimelineOptions
): TimelineData {
  // Support both legacy (string) and new (options object) signatures
  const options: PreviewToTimelineOptions =
    typeof labelFieldOrOptions === 'object' && labelFieldOrOptions !== null
      ? labelFieldOrOptions
      : { labelField: labelFieldOrOptions }

  const { labelField, showNewPrefix } = options
  const { newRecord, supersessions = [], splices = [], continuations = [] } = previewData

  // Build family map: supersession uid -> family index (0-11, cycling)
  const familyMap = new Map<string, number>()
  supersessions.forEach((s, index) => {
    familyMap.set(s.uid, index % 12)
  })

  // === BEFORE Timeline ===
  // Shows all supersessions in their current (active) state
  const beforeSegments: TimelineSegment[] = supersessions.map((s) => ({
    uid: s.uid,
    name: getLabel(s.record, labelField),
    effectiveFrom: s.effectiveFrom,
    effectiveTo: s.effectiveTo,
    familyIndex: familyMap.get(s.uid) ?? 0,
    type: 'active' as const,
    isSelectable: true,
    record: s.record,
    zIndex: 1,
  }))

  // === AFTER Timeline ===
  const afterSegments: TimelineSegment[] = []

  // 1. Add PRE-splice segments (records truncated before NEW)
  // NOTE: Timeline uses actual half-open interval dates for bar positioning.
  // The bar extends TO the effectiveTo boundary (exclusive), making it visually
  // contiguous with the next segment that starts at that same boundary.
  // The table display handles -1 day adjustment for showing "last valid day" text.
  for (const splice of splices) {
    if (splice.type !== 'pre') continue

    const sourceRecord = supersessions.find((s) => s.uid === splice.sourceUid)

    afterSegments.push({
      uid: splice.sourceUid,
      name: sourceRecord?.record ? getLabel(sourceRecord.record, labelField) : splice.name,
      effectiveFrom: splice.effectiveFrom,
      effectiveTo: splice.effectiveTo, // Use actual half-open interval end for positioning
      familyIndex: familyMap.get(splice.sourceUid) ?? 0,
      type: 'superseded',
      isSelectable: true,
      record: sourceRecord?.record ?? { name: splice.name },
      zIndex: 1,
    })
  }

  // NOTE: Completely overwritten records (no PRE-splice) are NOT added to AFTER
  // timeline - they disappear entirely, replaced by NEW. They only appear in
  // BEFORE to show what existed, and in the comparison table with strikethrough
  // styling to indicate they're being removed.

  // 2. Add the NEW record (always focus color, highest z-index)
  // isSelectable: true allows clicking to show educational empty state in diff panel
  const baseNewLabel = getLabel(newRecord as Record<string, unknown>, labelField)
  const newSegmentLabel = showNewPrefix
    ? (baseNewLabel ? `NEW - ${baseNewLabel}` : 'NEW')
    : baseNewLabel

  // NULL effectiveFrom means "effective immediately" - normalize to NOW for timeline positioning
  // This matches the backend semantic convention used in conflict detection
  const normalizedEffectiveFrom = newRecord.effectiveFrom ?? new Date().toISOString()

  afterSegments.push({
    uid: newRecord.uid,
    name: newSegmentLabel,
    effectiveFrom: normalizedEffectiveFrom,
    effectiveTo: newRecord.effectiveTo,
    familyIndex: -1, // Focus color
    type: 'new',
    isSelectable: true,
    record: newRecord as Record<string, unknown>,
    zIndex: 2,
  })

  // 3. Add continuation records
  for (const cont of continuations) {
    if (cont.willCreate) {
      afterSegments.push({
        uid: `continuation-${cont.sourceUid}`,
        name: cont.preview ? getLabel(cont.preview, labelField) : 'Resumed',
        effectiveFrom: cont.effectiveFrom,
        effectiveTo: cont.effectiveTo,
        familyIndex: familyMap.get(cont.sourceUid) ?? 0,
        type: 'continuation',
        isSelectable: true,
        record: cont.preview || {},
        zIndex: 1,
      })
    }
  }

  // Sort both timelines by effectiveFrom
  // NULL effectiveFrom means "effective immediately" - use NOW for sorting
  const nowTimestamp = Date.now()
  const sortByDate = (a: TimelineSegment, b: TimelineSegment) => {
    const aFrom = a.effectiveFrom ? new Date(a.effectiveFrom).getTime() : nowTimestamp
    const bFrom = b.effectiveFrom ? new Date(b.effectiveFrom).getTime() : nowTimestamp
    return aFrom - bFrom
  }

  beforeSegments.sort(sortByDate)
  afterSegments.sort(sortByDate)

  return {
    before: {
      segments: beforeSegments,
      label: 'BEFORE',
    },
    after: {
      segments: afterSegments,
      label: 'AFTER',
    },
    newRecord: {
      uid: newRecord.uid,
      effectiveFrom: newRecord.effectiveFrom,
      effectiveTo: newRecord.effectiveTo,
      name: newRecord.name,
    },
  }
}

/**
 * Merge full history into timeline data (future feature).
 *
 * Adds background segments from a list() query that aren't already
 * in the affected segments.
 */
export function mergeFullHistoryIntoTimeline(
  baseData: TimelineData,
  fullHistory: Array<{
    uid: string
    name: string
    effectiveFrom: string | null
    effectiveTo: string | null
  }>
): TimelineData {
  // Get UIDs of all affected segments
  const affectedUids = new Set<string>()

  if (baseData.before) {
    baseData.before.segments.forEach((s) => affectedUids.add(s.uid))
  }
  baseData.after.segments.forEach((s) => {
    // Handle continuation UIDs
    if (s.uid.startsWith('continuation-')) {
      affectedUids.add(s.uid.replace('continuation-', ''))
    } else {
      affectedUids.add(s.uid)
    }
  })

  // Filter out affected records from full history
  const backgroundRecords = fullHistory.filter((r) => !affectedUids.has(r.uid))

  // Convert to background segments
  const backgroundSegments: TimelineSegment[] = backgroundRecords.map((r) => ({
    uid: r.uid,
    name: r.name,
    effectiveFrom: r.effectiveFrom,
    effectiveTo: r.effectiveTo,
    familyIndex: undefined, // Gray for background
    type: 'expired' as const,
    isSelectable: true,
    zIndex: 0,
  }))

  // Add background segments to both timelines
  return {
    ...baseData,
    before: baseData.before
      ? {
          ...baseData.before,
          backgroundSegments,
        }
      : undefined,
    after: {
      ...baseData.after,
      backgroundSegments,
    },
  }
}

/**
 * Get all segments from a TimelineData object (for domain calculation)
 */
export function getAllSegments(data: TimelineData): TimelineSegment[] {
  const segments: TimelineSegment[] = []

  if (data.before) {
    segments.push(...data.before.segments)
    if (data.before.backgroundSegments) {
      segments.push(...data.before.backgroundSegments)
    }
  }

  segments.push(...data.after.segments)
  if (data.after.backgroundSegments) {
    segments.push(...data.after.backgroundSegments)
  }

  return segments
}
